/**
 * Translates the discussion URLs that earlier digest emails sent out.
 *
 * Those links look like
 * `/members-discussions#/questions/chro-association/<url-subject>`, a client-side route the
 * previous UI understood. The fragment is never sent to the server, so nothing on the server can
 * redirect them: they have to be rewritten in the browser into ordinary widget navigation.
 *
 * Emails already delivered cannot be recalled, so this has no expiry.
 */

/**
 * Shared with the server: the portlet is non-instanceable, so its namespace is fixed.
 *
 * @see org.chroa.portal.message.boards.constants.DiscussionBoardPortletKeys
 */
export const PORTLET_NAME = 'chroa_discussion_board_portlet';

/** @see org.chroa.portal.message.boards.constants.DiscussionBoardMVCCommandNames */
const RENDER_THREAD_DETAIL = '/discussion_board/thread/detail';
const RENDER_THREAD_CREATE = '/discussion_board/thread/create';

/** The one subject the old route reserved for "start a new discussion". */
const NEW_THREAD_ROUTE = 'new';

/**
 * The one legacy route worth translating: a single thread.
 *
 *      #/questions/chro-association/how-do-you-staff-hr-analytics
 *        \_______/ \______________/ \___________________________/
 *        literal   community        url subject -> captured
 *
 * The community segment is matched but thrown away. It only ever held one value, the board is
 * resolved from the page instead, and the subject alone identifies the thread -- but it still
 * has to be *matched*, so that a fragment with the wrong number of segments is rejected rather
 * than having its parts silently slide along by one.
 *
 * Anchored at both ends, because a partial match is a fragment this code does not understand,
 * and acting on one would navigate somewhere unintended. `\/?$` tolerates a trailing slash,
 * which some mail clients append.
 *
 * The character class is the alphabet Liferay's url subjects are drawn from, and it is
 * deliberately narrow: it excludes `/`, which is what makes the segment split unambiguous, and
 * it excludes everything that could turn a fragment into a different URL. Anything outside it
 * fails to match and is left alone. Note `%` is *in* the set: Liferay percent-encodes into the
 * value it stores, so `%` is part of the subject rather than an escape introduced by the URL --
 * `30%25-ruling-in-the-netherlands` is the literal stored subject of a real thread. That is also
 * why nothing here decodes: the fragment text is already the value the server must be handed,
 * and decoding it would look up a subject that does not exist.
 *
 * The two classes are separated by a literal `/` that neither contains, so matching is linear --
 * there is no ambiguity for the engine to backtrack over.
 */
const LEGACY_ROUTE = /^#\/questions\/[A-Za-z0-9._~%*-]+\/([A-Za-z0-9._~%*-]+)\/?$/;

/**
 * The bare "start a new discussion" route, `#/new`, as a handful of older links spell it.
 *
 * The far more common spelling puts it in the subject position of the route above
 * (`#/questions/chro-association/new`); that one is caught by the NEW_THREAD_ROUTE check in
 * `translateLegacyRoute` rather than here, since it matches LEGACY_ROUTE first.
 */
const LEGACY_NEW_ROUTE = /^#\/new\/?$/;

const param = (name: string) => `_${PORTLET_NAME}_${name}`;

/**
 * The widget root, as the deep-link handling selects it.
 *
 * Presence of the board on the page is what makes translating safe. The fragment rides along on
 * every redirect hop the browser follows -- including onto the sign-in page, which loads this
 * same global bundle -- and translating it there would navigate away from sign-in and take the
 * login redirect parameter with it, stranding exactly the signed-out visitor this exists for.
 */
const BOARD_SELECTOR = '.chroa-discussion-board';

const buildUrl = (renderParameters: Record<string, string>): string => {
    const url = new URL(location.href);

    // The board is the page the visitor is already on -- the guard above establishes that -- so
    // reading the path keeps this free of a second hard-coded page address that could drift from
    // the digest's configured one. Existing query parameters are kept: a link out of an email may
    // carry campaign or click-tracking parameters that are not ours to discard.
    url.hash = '';

    // Stale widget state from an earlier navigation would fight the parameters set below -- a
    // leftover page number, say -- so the widget's own parameters are cleared while anything
    // foreign is left alone.
    [...url.searchParams.keys()]
        .filter((name) => name.startsWith('p_p_') || name.startsWith(`_${PORTLET_NAME}_`))
        .forEach((name) => url.searchParams.delete(name));

    url.searchParams.set('p_p_id', PORTLET_NAME);
    url.searchParams.set('p_p_lifecycle', '0');

    Object.entries(renderParameters).forEach(([name, value]) => {
        url.searchParams.set(param(name), value);
    });

    return url.toString();
};

/**
 * @return the widget URL this fragment means, or null if the fragment is not a legacy route
 */
export const translateLegacyRoute = (hash: string): string | null => {
    if (LEGACY_NEW_ROUTE.test(hash)) {
        return buildUrl({ mvcRenderCommandName: RENDER_THREAD_CREATE });
    }

    const match = LEGACY_ROUTE.exec(hash);

    if (!match) {
        return null;
    }

    const urlSubject = match[1];

    if (urlSubject === NEW_THREAD_ROUTE) {
        return buildUrl({ mvcRenderCommandName: RENDER_THREAD_CREATE });
    }

    return buildUrl({
        mvcRenderCommandName: RENDER_THREAD_DETAIL,
        messageUrlSubject: urlSubject,
    });
};

/**
 * Rewrites a legacy fragment in the address bar into widget navigation.
 *
 * Runs as early as the DOM allows -- before the widget's own deep-link handling can overwrite the
 * fragment -- and again on hashchange, for a link pasted into an open page or followed across a
 * SPA navigation.
 */
export const initLegacyLinks = (): void => {
    const run = () => {
        if (!location.hash) {
            return;
        }

        // Only on the board itself. Any other page carrying this fragment is a waypoint -- the
        // sign-in page above all -- and navigating away from it loses more than it fixes.
        if (!document.querySelector(BOARD_SELECTOR)) {
            return;
        }

        const target = translateLegacyRoute(location.hash);

        if (!target) {
            return;
        }

        // replace, not assign: the legacy URL must not sit in history, or Back would land on it
        // and translate again.
        location.replace(target);
    };

    // Module scripts are deferred, so the document is normally parsed by now and this resolves
    // synchronously -- which is what keeps it ahead of the deep-link handling registered after
    // it. The listener is only a safety net for a non-deferred load.
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
        run();
    }

    window.addEventListener('hashchange', run);
};
